<script setup lang="ts">
import { useConfirmTransaction } from '@/helpers/solana/useConfirmTransaction';
import { useSolBalance } from '@/helpers/solana/useSolBalance';
import { useStakedAmount } from '@/helpers/solana/useStakedAmount';
import { useWallet } from 'solana-wallets-vue';
import { prepareStakeTransaction } from '@/helpers/solana/prepareStakeTransaction';
import { ref } from 'vue';

const { connected, publicKey } = useWallet();
const { confirmTransaction } = useConfirmTransaction();
const { execute: refreshSolBalance } = useSolBalance();
const { execute: refreshStakedAmount } = useStakedAmount();

const loading = ref(false);

const amount = defineModel<number>({
  required: true,
});

const isFailed = ref(false);

const emit = defineEmits(['success']);

const sendStakingTransaction = async () => {
  try {
    loading.value = true;
    isFailed.value = false;
    if (!publicKey.value) throw new Error('Wallet not connected');
    if (!amount) throw new Error('Amount is required');

    const lamports = amount.value * 10 ** 9;
    const txn = await prepareStakeTransaction(publicKey.value, lamports);

    await confirmTransaction(txn, 'Staking confirmed');

    await Promise.all([refreshSolBalance(), refreshStakedAmount()]).then();
    amount.value = 0;

    emit('success');
  } catch (error) {
    // TODO Capture error in Sentry
    console.error(error);
    isFailed.value = true;
  } finally {
    loading.value = false;
  }
};

// WalletMultiButton doesn't export click method
// So we need to use this hack
const connectWallet = () => {
  const button = document.getElementsByClassName('swv-button-trigger')[0] as
    | HTMLButtonElement
    | undefined;
  button?.click();
};
</script>

<template>
  <div class="p-8 pt-0">
    <v-btn
      size="large"
      color="primary"
      variant="elevated"
      @click="connectWallet"
      class="w-full"
      v-if="!connected"
    >
      Connect your wallet
    </v-btn>
    <v-btn
      size="large"
      color="primary"
      variant="elevated"
      :loading="loading"
      :disabled="!amount || loading"
      class="w-full"
      @click="sendStakingTransaction"
      v-else
    >
      {{ !amount ? 'Select an amount' : `Stake ${amount} SOL` }}
    </v-btn>
    <p v-if="isFailed" class="text-red-500 text-center p-2">
      Transaction failed. Please try again.
    </p>
  </div>
</template>
